/**
 * Queue Track Row Component
 *
 * Displays an individual track in the playback queue with controls to play or remove it.
 * Shows track metadata including artist, title, and remix information in a compact row format.
 */
import { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";

/* Contexts */
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

/* Store */
import { updateQueue } from "../../store";

/* Assets */
import RemoveFromQueueIcon from "../../assets/icons/player/remove-from-queue.svg";

/* Components */
import PlayButton from "./PlayButton";

/**
 * QueueTrackRow Component
 *
 * @param {Object} trackMetadata - Core track information (title, artist)
 * @param {Object} remixMetadata - Additional data for remixes, edits and featured artists
 * @param {Array} playlistTracks - Collection of tracks in the current playlist
 * @param {Number} playIndex - Index position of this track in the queue
 * @returns {JSX.Element} Queue track row component
 */
const QueueTrackRow = ({
	trackMetadata,
	remixMetadata,
	playlistTracks,
	playIndex,
	thisPlaylistName,
}) => {
	/**
	 * Renders artist names with proper formatting and separators
	 *
	 * @param {String|Array} artists - Single artist name or array of artist names
	 * @param {String} prefix - Optional text to display before artist names (e.g., "ft.")
	 * @param {String} keyPrefix - Unique identifier for React key generation
	 * @returns {JSX.Element|null} Formatted artist names or null if no artists
	 */

	const [showBorder, setShowBorder] = useState(false);

	const renderArtists = (artists, prefix = "", keyPrefix = "") => {
		if (!artists) return null;

		const list = Array.isArray(artists) ? artists : [artists];

		if (list.length === 0) return null;

		return (
			<>
				{prefix && <span className="ml-1">{prefix}</span>}
				{list.map((artist, idx) => (
					<span
						key={`${keyPrefix}-${idx}`}
						className="cursor-default ml-0"
					>
						<span className="">{artist}</span>
						{idx < list.length - 1
							? idx === list.length - 2
								? " & "
								: ", "
							: ""}
					</span>
				))}
			</>
		);
	};

	// Access player context for queue management
	const {
		currentIndex,
		playerQueue,
		setPlayerQueue,
		removeFromQueue,
		playingRowType,
		playlistName,
		supremeCycle,
		currentTrack
	} = useSupremeEditsPlayer();

	const dispatch = useDispatch();

	/**
	 * Removes a track from the queue by its track and queue IDs
	 * Uses the context helper to properly adjust currentIndex
	 *
	 * @param {String} trackId - ID of the track to remove
	 * @param {String} queueTrackId - Queue-specific ID of the track
	 */
	const removeSongByIndx = (trackId, queueTrackId) => {
		// Find the index of the track to remove
		const indexToRemove = playerQueue.findIndex((track) =>
			track.trackId === trackId && track.queueTrackId === queueTrackId
		);

		if (indexToRemove !== -1) {
			// Use the context helper to remove and adjust indices
			removeFromQueue(indexToRemove);

			// Update Redux store with the new queue after removal
			const newQueue = playerQueue.filter((_, index) => index !== indexToRemove);
			dispatch(updateQueue({ tracks: newQueue }));
		}

		// Trigger border update to ensure UI refreshes correctly
		setTimeout(() => borderHandler(), 0);
	};

	const borderHandler = () => {
		setShowBorder(
			supremeCycle[currentIndex]?.trackId === trackMetadata?.trackId &&
			supremeCycle[currentIndex]?.queueTrackId ===
			trackMetadata?.queueTrackId &&
			playingRowType === "queue" &&
			playlistName === thisPlaylistName
		);
	};

	useEffect(() => {
		borderHandler();
	}, [
		supremeCycle,
		playerQueue,
		currentIndex,
		currentTrack,
		trackMetadata,
		playingRowType,
		playlistName,
		thisPlaylistName,
		playIndex,
		remixMetadata,
	]);

	return (
		<div className="relative flex flex-row w-full h-[60px] md:h-[80px] content-center justify-center items-center gap-3 md:gap-6 rounded-lg"
			onClick={
				() => console.log({ trackMetadata, remixMetadata, currentTrack })
			}
		>
			{/* Wrapper for border highlight */}
			<div
				className={`${showBorder
						? "border-gradient opacity-100"
						: "bg-transparent p-[1px] opacity-0"
					} transition-opacity duration-500 animate-opacity-scale z-0 absolute w-full h-full top-0 max-h-[60px] md:max-h-[80px]`}
			/>
			<div className="z-10 relative flex justify-center place-items-center w-full h-full p-[1px]">
				<div
					className={`relative z-10 group/track-row flex flex-row w-full h-full p-[1px] px-2 md:px-4 py-[6px] md:py-[10px] content-center justify-center items-center gap-3 md:gap-5 rounded-lg ${showBorder ? "bg-[#121517]" : ""
						}`}
				>
					{/* Play control area */}
					<div className="group w-6 h-6 md:w-8 md:h-8 relative cursor-pointer">
						<PlayButton
							className="absolute opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 group-hover:opacity-100 transition-all duration-500 cursor-pointer"
							metaData={trackMetadata}
							remixMetadata={remixMetadata}
							playlistTracks={playlistTracks}
							playIndex={playIndex}
							type="queue"
							thisPlaylistName={thisPlaylistName}
						/>
					</div>

					{/* Track metadata display area */}
					<div className="playlist-info flex flex-1 flex-col gap-0 justify-start content-center items-start overflow-hidden max-w-full w-[min(200px,100%)]">
						{/* Artist and featured artist names with proper formatting */}
						<p className="text-tertiary text-[10px] md:text-xs whitespace-nowrap max-w-full truncate overflow-hidden">
							{renderArtists(
								[
									...(trackMetadata?.artists || []), 
									...(remixMetadata?.additionalMainArtists || [])
								], 
								"", 
								"main"
							)}
							{remixMetadata?.featuredArtists &&
								renderArtists(
									remixMetadata?.featuredArtists,
									"ft. ",
									"featured"
								)}
						</p>

						{/* Track title with optional edit title */}
						<p className="flex flex-col justify-start items-start text-xs md:text-sm overflow-hidden whitespace-nowrap max-w-full">
							<span className="text-left font-semibold overflow-hidden w-fit max-w-full flex-1 truncate">
								{trackMetadata?.trackTitle}
							</span>
							{trackMetadata?.editInfos?.[0]?.editTitle && (
								<span className="text-left text-tertiary font-normal overflow-hidden w-fit max-w-full flex-1 truncate text-[10px] md:text-xs">
									({trackMetadata?.editInfos?.[0]?.editTitle})
								</span>
							)}
						</p>
					</div>

					{/* Remove from queue button */}
					<Tooltip
						title={"Remove from queue"}
						placement="top"
						slotProps={{
							tooltip: {
								sx: {
									color: "#8D8D8D",
									backgroundColor: "#1A1924",
								},
							},
							arrow: {
								sx: {
									color: "#1A1924",
								},
							},
						}}
						arrow
					>
						<div
							className="relative"
							onClick={() =>
								removeSongByIndx(
									trackMetadata?.trackId,
									trackMetadata?.queueTrackId
								)
							}
						>
							<img
								src={RemoveFromQueueIcon}
								width={20}
								height={20}
								alt="Options Icon"
								className="cursor-pointer md:w-6 md:h-6"
							/>
						</div>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default QueueTrackRow;